"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import { motion, AnimatePresence, Variants } from "framer-motion";
import RichText from "@/utils/richText";

interface IProps {
  highlights: { title: string; description?: string }[];
  imageGallery: { image: string }[];
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const highlightVariants: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const PackageHighlights = ({ highlights, imageGallery }: IProps) => {
  const [active, setActive] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  const totalSlides = highlights.length;
  const hasImages = imageGallery && imageGallery.length > 0;

  const startAutoPlay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % totalSlides);
    }, 9000);
  }, [totalSlides]);

  const handleInteraction = (index: number) => {
    setActive(index);
    startAutoPlay();
  };

  useEffect(() => {
    startAutoPlay();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startAutoPlay]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16 relative">
      <div className="flex flex-col lg:flex-row gap-12 items-start min-h-[600px]">
        <motion.div
          className="lg:w-1/2 space-y-8 z-10 w-full"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={containerVariants}
        >
          <div className="space-y-4">
            <motion.h4
              variants={highlightVariants}
              className="text-primary-500 font-black uppercase tracking-[0.4em] text-xs flex items-center gap-3"
            >
              <span className="w-12 h-[2px] bg-primary-500" />
              Exploration
            </motion.h4>
            <motion.h2
              variants={highlightVariants}
              className="text-3xl md:text-5xl font-black text-gray-900 tracking-tighter leading-none"
            >
              PACKAGE HIGHLIGHTS
            </motion.h2>
          </div>

          <div className="flex flex-col w-full">
            {highlights.map((item, index) => {
              const isActive = active === index;
              return (
                <motion.div
                  key={index}
                  variants={highlightVariants}
                  onClick={() => handleInteraction(index)}
                  className="group flex gap-4 pr-6 cursor-pointer relative"
                >
                  <div className="flex flex-col items-center">
                    <motion.div
                      animate={{
                        backgroundColor: isActive ? "#ea9530" : "#d1d5db",
                        scale: isActive ? 1.25 : 1,
                      }}
                      className="w-3 h-3 rounded-full border-2 mt-2 z-10 transition-colors duration-300 border-white shadow-sm"
                    />
                    {index !== totalSlides - 1 && (
                      <div className="w-[1.5px] h-full bg-gray-100 absolute top-4 bottom-0" />
                    )}
                  </div>

                  <div className="pb-10 pt-1 flex-1">
                    <p
                      className={`font-black text-[10px] uppercase tracking-[0.2em] mb-1 transition-colors duration-300 ${
                        isActive ? "text-primary-500" : "text-gray-400"
                      }`}
                    >
                      Highlight {String(index + 1).padStart(2, '0')}
                    </p>
                    <h3
                      className={`font-bold text-xl md:text-2xl transition-colors duration-300 ${
                        isActive ? "text-gray-900" : "text-gray-400"
                      }`}
                    >
                      {item?.title}
                    </h3>

                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: "easeInOut" }}
                          className="overflow-hidden mt-3"
                        >
                          <RichText
                            content={item.description || "Discover the unique experiences included in this package."}
                            className="text-gray-600 leading-relaxed text-sm md:text-base max-w-md"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          className="lg:w-1/2 relative w-full aspect-video lg:aspect-[1.4/1] flex items-center justify-center lg:sticky lg:top-32"
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="relative w-full h-full overflow-hidden rounded-[2.5rem] shadow-2xl bg-gray-50 border border-gray-100">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0"
              >
                {hasImages && (
                   <Image
                    src={imageGallery[active % imageGallery.length]?.image || ""}
                    alt="Highlight view"
                    fill
                    className="object-cover"
                    priority
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              </motion.div>
            </AnimatePresence>

            <div className="absolute right-6 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-20 max-h-[80%] overflow-y-auto no-scrollbar py-4">
              {highlights.map((_, i) => (
                <button
                  key={i}
                  onClick={() => handleInteraction(i)}
                  className="w-1 rounded-full transition-all duration-500 overflow-hidden bg-white/20 backdrop-blur-md shrink-0"
                  style={{ height: i === active ? "24px" : "6px" }}
                >
                  <motion.div
                    className="w-full bg-primary-500"
                    initial={{ height: 0 }}
                    animate={{ height: i === active ? "100%" : "0%" }}
                    transition={{ duration: i === active ? 9 : 0.3, ease: "linear" }}
                  />
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PackageHighlights;